import { computed, inject, Injectable, Signal, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type JsonType } from 'posthog-js';
import { firstValueFrom } from 'rxjs';
import { PostHogService } from './posthog.service';
import { POSTHOG_SSR_FLAG_STATE } from './posthog-flag-state';
import { PostHogFlagKey } from './posthog-flag-keys';

export type PostHogFlagValue = boolean | string;

/**
 * Every read goes through the SDK because the SDK call is what logs the exposure; serving a cached
 * snapshot would render the right arm and record nobody. The computeds stay lazy for the same
 * reason — reading a flag anywhere but where it renders dilutes the experiment.
 */
@Injectable({ providedIn: 'root' })
export class PostHogFlagsService {
  private readonly postHog = inject(PostHogService);

  /** Server render only. Without it the markup is always the control arm the browser hydrates out of. */
  private readonly ssrFlagState = inject(POSTHOG_SSR_FLAG_STATE, { optional: true });

  private readonly version = signal(0);
  private readonly values = new Map<string, Signal<PostHogFlagValue | undefined>>();
  private readonly payloads = new Map<string, Signal<JsonType>>();

  public readonly ready = computed(() => this.version() > 0 || !!this.ssrFlagState);

  constructor() {
    // `client` is undefined until the initialiser that starts the SDK runs, so reading it here
    // would wire this to nothing.
    this.postHog.settled$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.postHog.client?.onFeatureFlags(() => this.version.update((version) => version + 1));
    });
  }

  /** A boolean flag, or the chosen arm of a multivariate one. */
  public flag<T extends PostHogFlagValue>(key: PostHogFlagKey, fallback: T): Signal<T> {
    const value = this.rawValue(key);

    // SAFETY: `fallback` fixes T, and the union member the SDK returns is the arm the caller asked
    // for; PostHog has no type for the arms of a given key.
    return computed(() => (value() ?? fallback) as T);
  }

  /** A flag whose useful content is its JSON payload rather than its value. */
  public config<T extends JsonType>(key: PostHogFlagKey, fallback: T): Signal<T> {
    const payload = this.rawPayload(key);

    // SAFETY: the payload's shape is declared in PostHog, not in the type system; `fallback` is the
    // caller's statement of what it agreed to.
    return computed(() => (payload() ?? fallback) as T);
  }

  /** For a caller that is not in a reactive context — a guard, a constructor, an event handler. */
  public read<T extends PostHogFlagValue>(key: PostHogFlagKey, fallback: T): T {
    return untracked(this.flag(key, fallback));
  }

  /** Resolves when flags are usable, or when the SDK is never going to run, so no caller hangs. */
  public whenReady(): Promise<void> {
    if (untracked(this.ready)) {
      return Promise.resolve();
    }

    return firstValueFrom(this.postHog.settled$).then(() => {
      const client = this.postHog.client;

      // posthog-js reports flag arrival through a callback, so this is where the stream ends.
      return client && new Promise<void>((resolve) => client.onFeatureFlags(() => resolve()));
    });
  }

  private rawValue(key: PostHogFlagKey): Signal<PostHogFlagValue | undefined> {
    const existing = this.values.get(key);

    if (existing) {
      return existing;
    }

    const created = computed(() => {
      this.version();

      const client = this.postHog.client;

      // No SDK on the server, so the Express layer's evaluation stands in. Decides markup only.
      return client ? client.getFeatureFlag(key) : this.ssrFlagState?.featureFlags[key];
    });

    this.values.set(key, created);

    return created;
  }

  private rawPayload(key: PostHogFlagKey): Signal<JsonType> {
    const existing = this.payloads.get(key);

    if (existing) {
      return existing;
    }

    const created = computed(() => {
      this.version();

      const client = this.postHog.client;

      return client ? client.getFeatureFlagPayload(key) : this.ssrFlagState?.featureFlagPayloads[key];
    });

    this.payloads.set(key, created);

    return created;
  }
}
